import { FC, useState } from "react";
import {
  Scene,
  TemplateElement,
  useDeleteSceneMutation,
  UserStudio,
  useUpdateSceneMutation,
} from "../../../../features/api/apiSlice";
import styles from "./SceneItem.module.css";
import { InputField } from "../../../../components/InputField";
import { Button } from "../../../../components/Button";
import { SubmitHandler, useForm } from "react-hook-form";
import cn from "classnames";
import { Icon } from "../../../../components/Icon/Icon";
import { ButtonGroup } from "../../../../components/ButtonGroup";
import cameraTemplateImg from "../../../../components/Actions/img/camera-template.svg";
import desktopImg from "../../../../components/Actions/img/desktop.svg";
import { BackgroundRenderer } from "../../RightButtons/BackgroundRenderer";
import { getStreamOrientation } from "../../../../utils/streamOrientation";

type ChangeTitleSceneFormValues = {
  sceneTitle: string;
};

interface SceneItemProps extends Scene {
  isActive: boolean;
  isPending: boolean;
  isTransitioning: boolean;
  onSceneSelect: (sceneId: number) => void;
  canDelete?: boolean;
  isShowEdit?: boolean;
  studio: UserStudio;
}

const SceneItem: FC<SceneItemProps> = ({
  id,
  title,
  background,
  current_template,
  isActive,
  isPending,
  isTransitioning,
  onSceneSelect,
  canDelete,
  isShowEdit,
  studio
}) => {
  const { isHorizontal } = getStreamOrientation(studio);
  const [deleteScene] = useDeleteSceneMutation();
  const [updateScene] = useUpdateSceneMutation();
  const [isEdit, setIsEdit] = useState(false);
  const { register, handleSubmit } = useForm<ChangeTitleSceneFormValues>({
    defaultValues: {
      sceneTitle: title,
    },
  });
  const elements = current_template?.elements || [];

  const convertToPxValue = (value: string | number | 'auto', defaultValue?: string): string => {
    if (value === 'auto') {
      return defaultValue || 'auto';
    }
    return typeof value === 'number' ? `${value}px` : value;
  };

  const handleSave: SubmitHandler<ChangeTitleSceneFormValues> = ({ sceneTitle }) => {
    updateScene({ id, title: sceneTitle.trim() });
    setIsEdit(false);
  };

  const handleEditTitle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEdit(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    deleteScene({ id });
  };

  const handleSceneClick = () => {
    if (isEdit || isActive || isTransitioning) {
      return;
    }

    onSceneSelect(id);
  };

  return (
    <div
      className={cn(styles.scene__item, {
        [styles.scene__item_active]: isActive,
        [styles.scene__item_pending]: isPending,
        [styles.scene__item_transitioning]: isTransitioning
      })}
      onClick={handleSceneClick}
    >
      <div
        className={cn(styles.scene__block, {
          [styles.scene__block_horizontal]: isHorizontal,
          [styles.scene__block_vertical]: !isHorizontal,
        })}
      >
        <BackgroundRenderer
          background={background}
          videoClassName={styles["scene__background-video"]}
          imageClassName={styles["scene__background-image"]}
        />
        {elements.map((element: TemplateElement, index: number) => (
          <div
            key={index}
            className={styles.scene__element}
            style={{
              top: convertToPxValue(element.top),
              left: convertToPxValue(element.left),
              width: convertToPxValue(element.width),
              height: convertToPxValue(element.height),
              zIndex: element.z_index || 1,
              display: element.display || "flex"
            }}
          >
            {element.type === "camera" ? (
              <img
                className={styles.samples__image}
                src={cameraTemplateImg}
                alt="Camera"
              />
            ) : (
              <img
                className={styles.samples__image}
                src={desktopImg}
                alt="Screen"
              />
            )}
          </div>
        ))}
      </div>
      <div
        className={cn(styles["scene__actions-container"], {
          [styles["scene__actions-container_rotate"]]: isEdit,
        })}
      >
        <div className={cn(styles["scene__info"], styles["scene__edit-info"])}>
          <form
            onSubmit={handleSubmit(handleSave)}
            className={styles["scene__info-form"]}
            onClick={(e) => e.stopPropagation()}
          >
            <InputField
              {...register("sceneTitle")}
              placeholder="Введите название"
              className={styles["scene__info-form-input"]}
            />
            <Button
              kind={"primary"}
              type="submit"
              className={styles["scene__save-button"]}
            >
              Сохранить
            </Button>
          </form>
        </div>
        <div
          className={cn(
            styles.scene__info,
            styles["scene__info-control-buttons"]
          )}
        >
          <span className={styles.scene__title}>
            {title || "Сцена без названия"}
          </span>
          <ButtonGroup className={styles["scene__button-group"]}>
            {isShowEdit &&
              <Button
                className={styles["scene__edit-button"]}
                onClick={handleEditTitle}
                kind="secondary"
              >
                <Icon name="studio-edit" />
              </Button>
            }
            {canDelete && isShowEdit && (
              <Button
                className={styles["scene__delete-button"]}
                onClick={handleDelete}
                kind="secondary"
              >
                <Icon name="studio-delete" />
              </Button>
            )}
          </ButtonGroup>
        </div>
      </div>
    </div>
  );
};

export default SceneItem;
